import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import type {
  Finding,
  FindingStatus,
  KillChainPhase,
  LogEntry,
  SeverityLevel,
} from '../../data-layer/insightDashboardTypes';
import {
  getSessionIdsForFinding,
  getSeverityColor,
} from '../../data-layer/insightDashboardUtils';

import FindingCard from './FindingCard';
import FindingDetailModal from './FindingDetailModal';

import './FindingsList.css';

interface FindingsListProps {
  findings: Finding[];
  logEntries: LogEntry[];
  phaseFilter: KillChainPhase | 'ALL';
  onPhaseChange: (phase: KillChainPhase | 'ALL') => void;
  onStatusChange: (id: number, status: FindingStatus) => void;
  selectedSession?: string;
  severityFilter?: SeverityLevel | 'ALL';
  onSeverityFilterChange?: (severity: SeverityLevel | 'ALL') => void;
  highlightedFindingId?: number | null;
  autoExpandFindingId?: number | null;
  sessionColorMap?: Record<string, string> | null;
  sliceVisibleFindingIds?: number[] | null;
  isGraphVisible?: boolean;
  onToggleGraph?: () => void;
  className?: string;
}

const defaultProps: Partial<FindingsListProps> = {
  selectedSession: undefined,
  severityFilter: undefined,
  onSeverityFilterChange: undefined,
  highlightedFindingId: undefined,
  autoExpandFindingId: undefined,
  sessionColorMap: undefined,
  sliceVisibleFindingIds: undefined,
  isGraphVisible: undefined,
  onToggleGraph: undefined,
  className: undefined,
};

type SeverityGroupKey = 'CRITICAL' | 'HIGH' | 'MEDIUM';

const PHASE_TABS: { key: KillChainPhase | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'reconnaissance', label: 'Recon' },
  { key: 'weaponization', label: 'Weaponize' },
  { key: 'exploitation', label: 'Exploit' },
  { key: 'anti_forensics', label: 'Anti-Forensics' },
];

const SEVERITY_ORDER: SeverityGroupKey[] = ['CRITICAL', 'HIGH', 'MEDIUM'];

const FINDINGS_TITLE = 'Findings';
const ATTACK_STAGES = 'Attack Stages';
const NO_FINDINGS_MSG = 'No findings match the current filter.';

/**
 * Filterable list of security findings for the Insight Dashboard: phase/severity/session filters,
 * inline expand per finding, optional highlight pulse, and detail panel with timeline and evidence.
 *
 * @param props - Component props.
 * @param props.findings - All findings to display.
 * @param props.logEntries - Log entries used to resolve session IDs and evidence.
 * @param props.phaseFilter - Active kill-chain phase filter.
 * @param props.onPhaseChange - Callback when the phase filter changes.
 * @param props.onStatusChange - Callback when a finding's status is updated.
 * @param props.selectedSession - Optional session ID to scope the findings list to.
 * @param props.severityFilter - Active severity filter. Defaults to `'ALL'`.
 * @param props.onSeverityFilterChange - Callback when the severity chip filter changes.
 * @param props.highlightedFindingId - ID of the finding to highlight with a pulse animation. Defaults to `null`.
 * @param props.className - Additional CSS class for the root element.
 * @returns The rendered findings list with header filters and expandable finding cards.
 */
function FindingsList({
  findings,
  logEntries,
  phaseFilter,
  onPhaseChange,
  onStatusChange,
  selectedSession,
  severityFilter = 'ALL',
  onSeverityFilterChange,
  highlightedFindingId = null,
  autoExpandFindingId = null,
  sessionColorMap = null,
  sliceVisibleFindingIds = null,
  isGraphVisible = false,
  onToggleGraph,
  className,
}: FindingsListProps) {
  const [expandedFindingId, setExpandedFindingId] = useState<number | null>(
    null
  );

  // Auto-expand finding when clicked from area chart or kill chain
  useEffect(() => {
    if (autoExpandFindingId !== null) {
      setExpandedFindingId(autoExpandFindingId);
    }
  }, [autoExpandFindingId]);

  const sessionMap = useMemo(() => {
    const map = new Map<number, string[]>();

    findings.forEach((f) => {
      map.set(f.id, getSessionIdsForFinding(f, logEntries));
    });

    return map;
  }, [findings, logEntries]);

  // Step 1: Filter by visible finding IDs from area chart slice
  const timeFilteredFindings = useMemo(() => {
    if (!sliceVisibleFindingIds) {
      return findings;
    }

    const idSet = new Set(sliceVisibleFindingIds);

    return findings.filter((f) => idSet.has(f.id));
  }, [findings, sliceVisibleFindingIds]);

  // Step 2: Apply severity, phase, session filters on top of time-filtered findings
  const filteredFindings = useMemo(() => {
    let result = timeFilteredFindings;

    if (phaseFilter !== 'ALL') {
      result = result.filter((f) => f.phase === phaseFilter);
    }

    if (severityFilter !== 'ALL') {
      result = result.filter((f) => f.severity === severityFilter);
    }

    if (selectedSession && selectedSession !== 'all') {
      result = result.filter((f) => {
        const sessions = sessionMap.get(f.id) ?? [];

        return sessions.length === 0 || sessions.includes(selectedSession);
      });
    }

    return result;
  }, [
    timeFilteredFindings,
    phaseFilter,
    severityFilter,
    selectedSession,
    sessionMap,
  ]);

  const handleFindingClick = useCallback((finding: Finding) => {
    setExpandedFindingId((prev) => (prev === finding.id ? null : finding.id));
  }, []);

  return (
    <Box className={cx('Sod-FindingsList', className)}>
      {/* Header */}
      <Box className='Sod-FindingsList-header'>
        <Box className='Sod-FindingsList-header-titleRow'>
          <Typography kind='h3' className='Sod-FindingsList-header-title'>
            {FINDINGS_TITLE}
          </Typography>

          {/* Severity chips */}
          <div className='Sod-FindingsList-severityChips'>
            {SEVERITY_ORDER.map((sev) => {
              const isActive = severityFilter === sev;
              const phaseScoped =
                phaseFilter !== 'ALL'
                  ? timeFilteredFindings.filter((f) => f.phase === phaseFilter)
                  : timeFilteredFindings;
              const count = phaseScoped.filter(
                (f) => f.severity === sev
              ).length;
              const colorToken = getSeverityColor(sev);

              return (
                <button
                  key={sev}
                  type='button'
                  className={cx('Sod-FindingsList-severityChip', {
                    'Sod-FindingsList-severityChip--active':
                      isActive || severityFilter === 'ALL',
                    'Sod-FindingsList-severityChip--unselected':
                      severityFilter !== 'ALL' && !isActive,
                  })}
                  style={
                    {
                      '--chip-color': `var(--id-severity-${colorToken})`,
                      '--chip-bg': `var(--id-severity-${colorToken}-bg)`,
                    } as React.CSSProperties
                  }
                  onClick={() => onSeverityFilterChange?.(sev)}>
                  <span className='Sod-FindingsList-severityChip-dot' />
                  {sev.charAt(0) + sev.slice(1).toLowerCase()}
                  <span className='Sod-FindingsList-severityChip-count'>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </Box>

        {/* Phase chips */}
        <div className='Sod-FindingsList-phaseChips'>
          <span className='Sod-FindingsList-phaseChips-label'>
            {ATTACK_STAGES}
          </span>
          {PHASE_TABS.map((tab) => {
            const isActive = phaseFilter === tab.key;

            return (
              <button
                key={tab.key}
                type='button'
                className={cx('Sod-FindingsList-phaseChip', {
                  'Sod-FindingsList-phaseChip--active': isActive,
                })}
                onClick={() => onPhaseChange(isActive ? 'ALL' : tab.key)}>
                {tab.label}
              </button>
            );
          })}
          {/* Graph toggle icon */}
          <button
            type='button'
            className={cx('Sod-FindingsList-graphToggle', {
              'Sod-FindingsList-graphToggle--active': isGraphVisible,
            })}
            onClick={onToggleGraph}
            title={isGraphVisible ? 'Hide attack graph' : 'Show attack graph'}>
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <circle
                cx='8'
                cy='8'
                r='3'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <circle cx='3' cy='3' r='1.5' fill='currentColor' />
              <circle cx='13' cy='3' r='1.5' fill='currentColor' />
              <circle cx='3' cy='13' r='1.5' fill='currentColor' />
              <circle cx='13' cy='13' r='1.5' fill='currentColor' />
              <line
                x1='5.5'
                y1='5.5'
                x2='4'
                y2='4'
                stroke='currentColor'
                strokeWidth='1'
              />
              <line
                x1='10.5'
                y1='5.5'
                x2='12'
                y2='4'
                stroke='currentColor'
                strokeWidth='1'
              />
              <line
                x1='5.5'
                y1='10.5'
                x2='4'
                y2='12'
                stroke='currentColor'
                strokeWidth='1'
              />
              <line
                x1='10.5'
                y1='10.5'
                x2='12'
                y2='12'
                stroke='currentColor'
                strokeWidth='1'
              />
            </svg>
          </button>
        </div>
      </Box>

      {/* Findings list — flat, driven by chips */}
      <Box className='Sod-FindingsList-items'>
        {filteredFindings.length === 0 && (
          <Box className='Sod-FindingsList-empty'>
            <Typography kind='body1' className='Sod-FindingsList-empty-text'>
              {NO_FINDINGS_MSG}
            </Typography>
          </Box>
        )}

        {[...filteredFindings]
          .sort((a, b) => b.riskScore - a.riskScore)
          .map((finding) => {
            const sessions = sessionMap.get(finding.id) ?? [];
            const isExpanded = expandedFindingId === finding.id;

            return (
              <div key={finding.id} className='Sod-FindingsList-cardWrapper'>
                <div className='Sod-FindingsList-cardRow'>
                  <FindingCard
                    finding={finding}
                    isCrossSession={sessions.length > 1}
                    sessionIds={sessions}
                    onClick={handleFindingClick}
                    onStatusChange={onStatusChange}
                    isHighlighted={highlightedFindingId === finding.id}
                    isExpanded={isExpanded}
                    className='Sod-FindingsList-card'
                  />
                </div>
                {isExpanded && (
                  <FindingDetailModal
                    finding={finding}
                    logEntries={logEntries}
                    onClose={() => setExpandedFindingId(null)}
                    isInline
                    sessionColorMap={sessionColorMap}
                  />
                )}
              </div>
            );
          })}
      </Box>
    </Box>
  );
}

FindingsList.defaultProps = defaultProps;

export default FindingsList;
